import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { BookOpen, Edit, Trash2, Save, FolderOpen } from 'lucide-react';

const DocsManager = () => {
  const queryClient = useQueryClient();
  const [editingDoc, setEditingDoc] = useState(null);
  
  // Form State
  const [form, setForm] = useState({
     title: '', slug: '', category: 'General', content_html: '', display_order: 0, is_published: true
  });

  const { data: docs = [] } = useQuery({
    queryKey: ['adminDocs'],
    queryFn: async () => {
        const res = await axiosClient.get('/admin/cms/docs');

        // Handle both res.data and direct res (if interceptor is used)
        const actualData =
        res.data !== undefined ? res.data : res;

        return Array.isArray(actualData) ? actualData : [];
    },
    });


  const saveMutation = useMutation({
  // Use the full relative path to match the router prefix
    mutationFn: (data) => axiosClient.post('/admin/cms/docs', data),

    onSuccess: () => {
        queryClient.invalidateQueries(['adminDocs']);
        resetForm();
        alert('Document Saved!');
    },

    onError: (error) => {
        const errorMsg =
        error.response?.data?.detail || error.message;

        console.error(
        'FULL ERROR OBJECT:',
        error.response
        );

        alert('Save Failed: ' + JSON.stringify(errorMsg));
    },
    });


  const deleteMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/cms/docs/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['adminDocs'])
  });

  const resetForm = () => {
      setEditingDoc(null);
      setForm({ title: '', slug: '', category: 'General', content_html: '', display_order: 0, is_published: true });
  };

  const handleEdit = (doc) => {
      setEditingDoc(doc.id);
      setForm(doc);
  };

  const handleSave = () => {
    if (!form.title) {
        alert('Title is required');
        return;
    }

    const payload = { ...form };

    // If id is empty string or null, remove it so Pydantic handles it
    if (!payload.id) delete payload.id;

    if (!payload.slug) {
        payload.slug = payload.title
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]/g, '');
    }

    console.log('Sending Payload:', payload);
    saveMutation.mutate(payload);
    };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT: EDITOR */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm h-fit">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-slate-800 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-teal-600"/> 
                    {editingDoc ? 'Edit Document' : 'Create New Document'}
                </h3>
                {editingDoc && <button onClick={resetForm} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>}
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title</label>
                        <input className="w-full p-2 border rounded" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Slug (URL)</label>
                        <input className="w-full p-2 border rounded bg-slate-50 font-mono text-xs" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="auto-generated" />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
                        <input className="w-full p-2 border rounded" list="categories" value={form.category} onChange={e => setForm({...form, category: e.target.value})} />
                        <datalist id="categories">
                            <option value="Getting Started" />
                            <option value="Installation" />
                            <option value="Troubleshooting" />
                            <option value="API Reference" />
                        </datalist>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Sort Order</label>
                        <input type="number" className="w-full p-2 border rounded" value={form.display_order} onChange={e => setForm({...form, display_order: parseInt(e.target.value)})} />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Content (HTML Supported)</label>
                    <textarea 
                        className="w-full p-3 border rounded h-64 font-mono text-sm" 
                        value={form.content_html} 
                        onChange={e => setForm({...form, content_html: e.target.value})}
                        placeholder="<h1>Title</h1><p>Content...</p>"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} />
                    <label className="text-sm text-slate-700">Publish Immediately</label>
                </div>

                <button 
                    onClick={handleSave} 
                    disabled={saveMutation.isLoading}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center"
                >
                    {saveMutation.isLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2"/> Save Document</>}
                </button>
            </div>
        </div>

        {/* RIGHT: LIST */}
        <div className="space-y-4">
            {/* Group by Category */}
            {Object.entries(docs.reduce((acc, doc) => {
                (acc[doc.category] = acc[doc.category] || []).push(doc);
                return acc;
            }, {})).map(([category, items]) => (
                <div key={category} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center font-bold text-slate-600 text-xs uppercase">
                        <FolderOpen className="w-3 h-3 mr-2"/> {category}
                    </div>
                    <div className="divide-y divide-slate-100">
                        {items.map(doc => (
                            <div key={doc.id} className="p-3 flex justify-between items-center hover:bg-slate-50 transition-colors group">
                                <div>
                                    <div className="font-medium text-slate-800 text-sm">{doc.title}</div>
                                    <div className="text-[10px] text-slate-400 font-mono">/{doc.slug}</div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEdit(doc)} className="p-1.5 text-teal-600 hover:bg-blue-100 rounded"><Edit className="w-3 h-3"/></button>
                                    <button onClick={() => {if(window.confirm('Delete?')) deleteMutation.mutate(doc.id)}} className="p-1.5 text-red-600 hover:bg-red-100 rounded"><Trash2 className="w-3 h-3"/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>

    </div>
  );
};

export default DocsManager;